import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Paper,
  List,
  ListItem,
  ListItemText,
  IconButton,
  CircularProgress,
  Chip,
  Alert,
  Divider,
  TextField,
  Grid
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import api from '../../services/api';
import { TESTS } from '../../config/Config';
import MathRenderer from '../common/MathRenderer';
import { canConfirmQuestionReview } from '../../utils/testQuestionReview';

const TestQuestionReviewDialog = ({
  open,
  onClose,
  testId,
  questions = [],
  onAssign,
  testData,
  studentToAssign,
  requireCompletionDate = true,
  confirmLabel = 'Assign Test',
}) => {
  const [replacingQuestionId, setReplacingQuestionId] = useState(null);
  const [updatedQuestions, setUpdatedQuestions] = useState(questions);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [targetDate, setTargetDate] = useState('');

  // Update questions when prop changes
  React.useEffect(() => {
    if (questions && questions.length > 0) {
      setUpdatedQuestions(questions);
    }
  }, [questions]);

  // Reset target date when dialog closes
  React.useEffect(() => {
    if (!open) {
      setTargetDate('');
      setError('');
    }
  }, [open]);

  const handleReplaceQuestion = async (oldQuestionId, topicId) => {
    if (!testId || !oldQuestionId || !topicId) {
      setError('Missing required information to replace question');
      return;
    }

    setReplacingQuestionId(oldQuestionId);
    setError('');
    
    try {
      const response = await api.post(`${TESTS}${testId}/replace_question/`, {
        old_question_id: oldQuestionId,
        topic_id: topicId
      });

      if (response.data && response.data.replacement_question) {
        // Update the question in the list
        const replacement = response.data.replacement_question;
        setUpdatedQuestions(prev => {
          const updated = prev.map(q => 
            q.id === oldQuestionId 
              ? replacement
              : q
          );
          return updated;
        });
        
        // Refresh all questions from API to ensure consistency
        if (testId) {
          try {
            const refreshResponse = await api.get(`${TESTS}${testId}/questions/`);
            const refreshedQuestions = Array.isArray(refreshResponse.data) 
              ? refreshResponse.data 
              : (refreshResponse.data.results || []);
            if (refreshedQuestions.length > 0) {
              setUpdatedQuestions(refreshedQuestions);
            }
          } catch (refreshErr) {
            console.error('Error refreshing questions:', refreshErr);
            // Continue with local update if refresh fails
          }
        }
      }
    } catch (err) {
      console.error('Error replacing question:', err);
      setError(
        err.response?.data?.error || 
        err.response?.data?.detail || 
        'Failed to replace question. Please try again.'
      );
    } finally {
      setReplacingQuestionId(null);
    }
  };

  const handleAssign = async () => {
    if (requireCompletionDate && !targetDate) {
      setError('Please select a completion date');
      return;
    }
    
    if (onAssign) {
      setLoading(true);
      try {
        await onAssign(testId, updatedQuestions, testData, studentToAssign, targetDate);
      } catch (err) {
        setError(err.message || 'Failed to assign test');
      } finally {
        setLoading(false);
      }
    }
  };

  const getQuestionTypeLabel = (type) => {
    const typeMap = {
      'MULTIPLE': 'Multiple Choice',
      'TRUE_FALSE': 'True/False',
      'SHORT_ANSWER': 'Short Answer',
      'ESSAY': 'Essay'
    };
    return typeMap[type] || type;
  };

  const getDifficultyLabel = (difficulty) => {
    if (!difficulty) return 'N/A';
    if (typeof difficulty === 'object') {
      return difficulty.name || difficulty.label || 'N/A';
    }
    return difficulty;
  };

  /** Human-readable confirmation of the picked date, shown under the field so the
   *  selection is visible even when the browser's native date widget renders oddly. */
  const formatSelectedDate = (value) => {
    const dt = new Date(`${value}T00:00:00`);
    return isNaN(dt.getTime())
      ? value
      : dt.toLocaleDateString('en-AU', { weekday: 'long', day: '2-digit', month: 'long', year: 'numeric' });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        Review Generated Questions
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Review the questions before {requireCompletionDate ? 'assigning' : 'saving'} the test. You can replace any question with another from the same topic.
        </Typography>
      </DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {/* Completion Date Field — mt keeps the floating label clear of the dialog title */}
        {requireCompletionDate && <Box sx={{ mb: 3, mt: 2 }}>
          <TextField
            fullWidth
            label="Completion Date *"
            type="date"
            value={targetDate}
            onChange={(e) => setTargetDate(e.target.value)}
            InputLabelProps={{
              shrink: true,
            }}
            inputProps={{
              min: new Date().toISOString().split('T')[0] // Prevent selecting past dates
            }}
            required
            error={!targetDate && loading === false}
            helperText={
              targetDate
                ? `Selected: ${formatSelectedDate(targetDate)}`
                : 'Please select a completion date for this assignment'
            }
          />
        </Box>}

        {updatedQuestions.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography color="text.secondary">No questions generated</Typography>
          </Box>
        ) : (
          <List>
            {updatedQuestions.map((question, index) => (
              <React.Fragment key={question.id}>
                <Paper 
                  elevation={1} 
                  sx={{ 
                    mb: 2, 
                    p: 2,
                    border: '1px solid',
                    borderColor: 'divider'
                  }}
                >
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                    <Box sx={{ flex: 1 }}>
                      <Box sx={{ display: 'flex', gap: 1, mb: 1, flexWrap: 'wrap' }}>
                        <Chip 
                          label={`Question ${index + 1}`} 
                          size="small" 
                          color="primary" 
                          variant="outlined"
                        />
                        <Chip 
                          label={getQuestionTypeLabel(question.question_type)} 
                          size="small" 
                          variant="outlined"
                        />
                        {question.difficulty && (
                          <Chip 
                            label={`Difficulty: ${getDifficultyLabel(question.difficulty)}`} 
                            size="small" 
                            variant="outlined"
                          />
                        )}
                        {(() => {
                          // Get topic name from various possible locations
                          let topicName = null;
                          if (question.topic_name) {
                            topicName = question.topic_name;
                          } else if (question.topic) {
                            if (typeof question.topic === 'object') {
                              topicName = question.topic.topic || question.topic.name || question.topic.topic_name;
                            } else {
                              // If topic is just an ID, we can't display name without fetching
                              // But we'll try to get it from topics array if available
                              if (question.topics && Array.isArray(question.topics) && question.topics.length > 0) {
                                topicName = question.topics[0].topic || question.topics[0].name || question.topics[0].topic_name;
                              }
                            }
                          } else if (question.topics && Array.isArray(question.topics) && question.topics.length > 0) {
                            topicName = question.topics[0].topic || question.topics[0].name || question.topics[0].topic_name;
                          }
                          
                          return topicName ? (
                            <Chip 
                              label={`Topic: ${topicName}`} 
                              size="small" 
                              variant="outlined"
                              color="secondary"
                            />
                          ) : null;
                        })()}
                        {question.marks && (
                          <Chip 
                            label={`${question.marks} marks`} 
                            size="small" 
                            variant="outlined"
                          />
                        )}
                      </Box>
                      <Box sx={{ mb: 1, wordBreak: 'break-word' }}>
                        <MathRenderer content={question.question_text || ''} variant="body1" />
                      </Box>
                      
                      {/* Show options for multiple choice */}
                      {question.question_type === 'MULTIPLE' && question.options && question.options.length > 0 && (
                        <Box sx={{ mt: 1, ml: 2 }}>
                          {question.options.map((opt, optIdx) => {
                            const optionText = typeof opt === 'object' 
                              ? (opt.text || opt.option_text || opt.label || `Option ${optIdx + 1}`)
                              : opt;
                            const isCorrect = typeof opt === 'object' 
                              ? opt.is_correct 
                              : (question.correct_answer && String(question.correct_answer) === String(optIdx + 1));
                            
                            return (
                              <Box key={optIdx} sx={{ color: isCorrect ? 'success.main' : 'text.secondary', fontWeight: isCorrect ? 600 : 400 }}>
                                <Typography component="span" variant="body2">{String.fromCharCode(65 + optIdx)}. </Typography>
                                <MathRenderer content={String(optionText)} variant="body2" />
                                {isCorrect && ' ✓'}
                              </Box>
                            );
                          })}
                        </Box>
                      )}
                      
                      {/* Show correct answer for other types */}
                      {question.question_type !== 'MULTIPLE' && question.correct_answer && (
                        <Box sx={{ mt: 1, p: 1, bgcolor: 'success.light', borderRadius: 1 }}>
                          <Typography variant="body2" component="span" sx={{ fontWeight: 600 }}>Correct Answer: </Typography>
                          <MathRenderer content={String(question.correct_answer)} variant="body2" sx={{ fontWeight: 600 }} />
                        </Box>
                      )}
                    </Box>
                    
                    <IconButton
                      color="primary"
                      onClick={() => {
                        const topicId = question.topic?.id || question.topic_id || question.topic;
                        if (topicId) {
                          handleReplaceQuestion(question.id, topicId);
                        } else {
                          setError('Topic information not available for this question');
                        }
                      }}
                      disabled={replacingQuestionId === question.id}
                      sx={{ ml: 2 }}
                      title="Replace with another question from the same topic"
                    >
                      {replacingQuestionId === question.id ? (
                        <CircularProgress size={24} />
                      ) : (
                        <RefreshIcon />
                      )}
                    </IconButton>
                  </Box>
                </Paper>
                {index < updatedQuestions.length - 1 && <Divider sx={{ mb: 2 }} />}
              </React.Fragment>
            ))}
          </List>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleAssign}
          variant="contained"
          disabled={!canConfirmQuestionReview({
            loading,
            questions: updatedQuestions,
            requireCompletionDate,
            targetDate,
          })}
          startIcon={loading ? <CircularProgress size={20} /> : <CheckCircleIcon />}
        >
          {loading ? (requireCompletionDate ? 'Assigning...' : 'Saving...') : confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TestQuestionReviewDialog;

